export function arrayToObject<T>(array: T[], key: keyof T): Record<string, T> {
  return array.reduce((obj, item) => ({ ...obj, [String(item[key])]: item }), {});
}

export function groupBy<T>(xs: T[], key: keyof T): Record<string, T[]> {
  return xs.reduce(
    (rv, x) => {
      const keyValue = String(x[key]);
      if (rv[keyValue]) {
        rv[keyValue].push(x);
        return rv;
      }
      return {
        ...rv,
        [keyValue]: [x],
      };
    },
    {} as Record<string, T[]>
  );
}

export function groupByArray<T>(xs: T[], key: keyof T): Record<string, T[]> {
  return xs.reduce(
    (rv, x) => {
      const values = x[key] as string[];
      values.forEach((value) => {
        if (rv[value]) {
          rv[value].push(x);
        } else {
          rv[value] = [x];
        }
      });
      return rv;
    },
    {} as Record<string, T[]>
  );
}

export function round(number: number, precision: number): number {
  const factor = 10 ** precision;
  return Math.round(number * factor) / factor;
}

/**
 * Round to a number of significant figures (default 2), dropping trailing zeros.
 * E.g. 2274 → 2300, 22.74 → 23, -4.37 → -4.4, 0.063 → 0.063, 0 → 0.
 */
export function toSignificant(number: number, digits = 2): number {
  if (number === 0 || !Number.isFinite(number)) return number;
  return Number(number.toPrecision(digits));
}

/** Format an area in m² for display (fr-FR, 1 decimal max), e.g. 12.34 → "12,3 m²". */
export function formatAreaM2(m2: number): string {
  return `${m2.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} m²`;
}

/**
 * A structure/greenhouse height (stored in cm) split into a value + unit for
 * display: shown in cm below 1 m, otherwise in metres (fr-FR, e.g. 150 → "1,5"·"m").
 */
export function heightPartsCm(cm: number): { value: string; unit: string } {
  return cm < 100 ? { value: String(cm), unit: "cm" } : { value: (cm / 100).toLocaleString("fr-FR"), unit: "m" };
}

/** Same as {@link heightPartsCm} as a single string ("1,5 m", "50 cm"); "" for a falsy height. */
export function formatHeightCm(cm: number | null | undefined): string {
  if (!cm) return "";
  const { value, unit } = heightPartsCm(cm);
  return `${value} ${unit}`;
}

/** Height given in metres (brush presets) → display string ("1,5 m", "50 cm"). */
export function formatHeightM(metres: number): string {
  return formatHeightCm(Math.round(metres * 100));
}

/** A surface (m²) as fr-FR: integer when whole ("52"), else decimal comma ("0,25"). */
export function formatSquareMetersValue(value: number): string {
  return value.toLocaleString("fr-FR", { maximumFractionDigits: 2 });
}

/** Same with the unit appended ("52 m²", "0,25 m²"). */
export function formatSquareMeters(value: number): string {
  return `${formatSquareMetersValue(value)} m²`;
}

/**
 * A weight given in kilograms, shown with the handiest unit: kg for ≥ 1 kg
 * ("1,2 kg", up to gram precision), grams below that ("800 g").
 */
export function formatWeightKg(kg: number): string {
  return kg >= 1 ? `${kg.toLocaleString("fr-FR", { maximumFractionDigits: 3 })} kg` : `${Math.round(kg * 1000)} g`;
}

/**
 * Round a number to a "nice" value whose granularity grows with the magnitude
 * — 0.1 under 1, nearest unit under 10, then 10s, 25s for the hundreds, 50s,
 * 100s, then 1000s past 10k. Useful to display approximate counts (e.g.
 * "~250 radishes", "≈ 1 g") without pretending to be precise.
 */
export function roundToNice(n: number): number {
  if (n < 1) return Math.round(n * 10) / 10;
  if (n < 10) return Math.round(n);
  if (n < 50) return Math.round(n / 10) * 10;
  if (n < 200) return Math.round(n / 25) * 25;
  if (n < 1000) return Math.round(n / 50) * 50;
  if (n < 10000) return Math.round(n / 100) * 100;
  return Math.round(n / 1000) * 1000;
}

export function capitalize(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

// Lowercase + strip diacritics for accent-insensitive search/compare (é → e, ç → c…).
export function normalizeForSearch(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

export function getFortnightString(date: Date): string {
  const month = date.toLocaleDateString(undefined, { month: "long" });
  if (date.getDate() === 1 || isLastDayOfMonth(date)) {
    return month;
  }
  if (isMidMonth(date)) {
    return `mi-${month}`;
  }
  return `${date.getDate()} ${month}`;
}

export function isLastDayOfMonth(date: Date): boolean {
  return new Date(date.getTime() + 86400000).getDate() === 1;
}

// Days near the middle of the month collapse to "mi-{month}".
function isMidMonth(date: Date): boolean {
  const day = date.getDate();
  return day >= 13 && day <= 18;
}

// A "fortnight boundary" is the 1st, a mid-month day, or the last day of a month —
// the points where `getFortnightString` collapses to a month or "mi-month".
function isFortnightBoundary(date: Date): boolean {
  return date.getDate() === 1 || isMidMonth(date) || isLastDayOfMonth(date);
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function getPeriodString(dateFrom: Date, dateTo: Date): string {
  const day = (d: Date) => d.toLocaleDateString(undefined, { day: "numeric", month: "long" });

  if (isSameDay(dateFrom, dateTo)) {
    return `Le ${day(dateFrom)}`;
  }

  // Both ends land on month boundaries → use the lighter "De … à …" form.
  if (isFortnightBoundary(dateFrom) && isFortnightBoundary(dateTo)) {
    return `De ${getFortnightString(dateFrom)} à ${getFortnightString(dateTo)}`;
  }

  return `Du ${day(dateFrom)} au ${day(dateTo)}`;
}
